import { Injectable } from '@angular/core';
import { FeatureFlagService } from './feature-flag.service';
import { BaseApiService } from './api';
import { filter, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { CountriesMap, CountryInterface } from '../dto';
import { ToastrService } from 'ngx-toastr';
import { LoadingWrapper } from 'src/app/shared/decorators';
import { CacheService } from './cache.service';

export const LOCATIONS_LOADING_TAG: string = 'locations-data-loader';

@Injectable({
    providedIn: 'root'
})
export class LocationsService {
    private locationsSubject = new BehaviorSubject<CountryInterface[]>(null);
    private locationsMapSubject = new BehaviorSubject<CountriesMap>(null);
    private latestLocations: CountryInterface[] = [];

    constructor(
        private baseApiService: BaseApiService,
        private toastr: ToastrService,
        private cacheService: CacheService
    ) { }

    get locations$(): Observable<CountryInterface[]> {
        return this.locationsSubject.asObservable();
    }

    get locationsMap$(): Observable<CountriesMap> {
        return this.locationsMapSubject.asObservable();
    }

    loadLocations() {
        return this.getLocationsData().pipe(
            switchMap((locations: CountryInterface[]) => {
                if (!locations || !locations.length) {
                    this.toastr.error('No locations found');
                    return of(null);
                }
                return of(locations);
            }),
            filter(Boolean)
        )
            .subscribe(
                (locations: CountryInterface[]) => {
                    this.locationsSubject.next(locations);
                    this.latestLocations = [...locations];
                    const countriesObject = locations.reduce((acc, { countryCode, countryName, states }) => (
                        {...acc,
                            [countryCode]: { name: countryName, states }
                        }
                    ), {});
                    this.locationsMapSubject.next(countriesObject)
                },
                () => {
                    this.toastr.error('Error loading locations');
                }
            );
    }

    getLatestLocations(): CountryInterface[] {
        return [...this.latestLocations];
    }

    @LoadingWrapper(LOCATIONS_LOADING_TAG)
    private getLocationsData(): Observable<CountryInterface[]> {
        return this.cacheService.getCachedObservable(
            'locations-data',
            this.baseApiService.get('service/contactcloud/locations')
        );
    }
}
